import {
  BROKER_ID_CLAIM,
  ROLES_CLAIM,
  SF_BROKER_ID_CLAIM,
  type TokenClaims,
} from "./claims";
import { isUserRole, type UserRole } from "./roles";

import type { FeatureFlagAttributes } from "@platform/feature-flags";

export type SignedInUser = {
  readonly status: "signedIn";
  readonly id: string;
  readonly email?: string;
  readonly displayName: string;
  readonly givenName?: string;
  readonly familyName?: string;
  readonly role: UserRole;
  /**
   * Every role in the token, supported or not, for apps that gate on a role of their
   * own (the golden-set tool's rumi-tester). `role` is the first supported one.
   */
  readonly roles: readonly string[];
  readonly brokerId?: string;
  // The broker's Salesforce id, set for brokers who sign in through Salesforce.
  readonly sfBrokerId?: string;
  readonly isAdmin: boolean;
  // Narrow on purpose: true only for Centro analyst staff. Gate analyst RFP UI on
  // hasAnalystCapabilities instead. See ADR 10.19.
  readonly isAnalyst: boolean;
  readonly isBroker: boolean;
  readonly isAgent: boolean;
  readonly isBrokerAnalyst: boolean;
  // Broker and agent only. broker_analyst is excluded so those users fall through to
  // the analyst RFP UI. See ADR 10.19.
  readonly isBrokerOrAgent: boolean;
  readonly isConsultant: boolean;
  readonly isAdminOrConsultant: boolean;
  // Analyst, admin, consultant, and broker_analyst: everyone who may use the
  // analyst RFP UI. See ADR 10.19.
  readonly hasAnalystCapabilities: boolean;
};

export type SignedOutUser = { readonly status: "signedOut" };

export type User = SignedInUser | SignedOutUser;

/** A signed-in session whose role the app doesn't support; it can't be used. */
export type UnsupportedRole = {
  readonly status: "unsupportedRole";
  readonly role: string | undefined;
};

const ANALYST_CAPABLE_ROLES: readonly UserRole[] = [
  "admin",
  "analyst",
  "consultant",
  "broker_analyst",
];

// Auth0 sets `name` to the email for password accounts, so the given and family
// names come first.
const displayNameFrom = (claims: TokenClaims): string =>
  claims.given_name && claims.family_name
    ? `${claims.given_name} ${claims.family_name}`
    : (claims.name ?? "");

const roleFlags = (role: UserRole) => ({
  isAdmin: role === "admin",
  isAnalyst: role === "analyst",
  isBroker: role === "broker",
  isAgent: role === "agent",
  isBrokerAnalyst: role === "broker_analyst",
  isBrokerOrAgent: role === "broker" || role === "agent",
  isConsultant: role === "consultant",
  isAdminOrConsultant: role === "admin" || role === "consultant",
  hasAnalystCapabilities: ANALYST_CAPABLE_ROLES.includes(role),
});

export interface UserFromClaimsOptions {
  /** Replaces the token's role, for local development only. */
  readonly roleOverride?: UserRole;
}

export const userFromClaims = (
  claims: TokenClaims,
  { roleOverride }: UserFromClaimsOptions = {},
): SignedInUser | UnsupportedRole => {
  const roles = claims[ROLES_CLAIM] ?? [];
  // The first supported role, so an app-specific role listed ahead of it (rumi-tester)
  // doesn't end the session.
  const role = roleOverride ?? roles.find(isUserRole);
  if (!isUserRole(role)) return { status: "unsupportedRole", role: roles[0] };

  return {
    status: "signedIn",
    id: claims.sub,
    email: claims.email ?? claims.emails?.[0],
    displayName: displayNameFrom(claims),
    givenName: claims.given_name,
    familyName: claims.family_name,
    role,
    roles,
    brokerId: claims[BROKER_ID_CLAIM],
    sfBrokerId: claims[SF_BROKER_ID_CLAIM],
    ...roleFlags(role),
  };
};

/** The attributes GrowthBook targets this user by; undefined without an email. */
export const featureFlagAttributesFor = (
  user: SignedInUser,
): FeatureFlagAttributes | undefined => {
  if (!user.email) return undefined;

  return {
    userId: user.id,
    role: user.role,
    email: user.email,
    ...(user.brokerId && { brokerId: user.brokerId }),
  };
};
